/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React from "react";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import { useForm } from "@/src/provider/Form/FormProvider";
import Typography from "@mui/material/Typography";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import wizardStyle from "@/src/components/Form/wizardStyle";
import { Box } from "@mui/material";
import { postalCodeDefault, requiredValidator } from "@/src/services/validationRules";
import Group from "@/src/components/Wizard/Group";
import { translate } from "@/src/services/translations/translations";
import EmployerTypeFederalIcon from "@/src/assets/icons/generic/toggleable/IconCourtDetailed";
import EmployerTypeStateIcon from "@/src/assets/icons/contract/toggleable/IconCompany";
import EmployerTypeMunicipalIcon from "@/src/assets/icons/provision/toggleable/IconHouse";
import ObjectiveMoneyAndRefundIcon from "@/src/assets/icons/contract/toggleable/IconEnforce";
import ObjectiveCareerAndEmploymentIcon from "@/src/assets/icons/employment/toggleable/IconMoneyAndWork";

import Address from "@/src/components/Form/Common/Address/Address";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import ValidatorSelectField from "@/src/components/FormFields/ValidatorSelectField";
import MenuItem from "@mui/material/MenuItem";
import { capitalizeFirstLetter } from "@/src/services/utils";

type EmploymentRelationshipProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

export default function EmploymentRelationship({ submitProps, product }: EmploymentRelationshipProps) {
  const { values, handleSubmit } = useForm();

  const employerStates = ["BW", "BY", "BE", "BB", "HB", "HH", "HE", "MV", "NI", "NW", "RP", "SL", "SN", "ST", "SH", "TH"] as const;

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Ihr Anliegen
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
            }}
          >
            <Grid size={12}>
              <FormLegend>
                <strong>Informationen zu Ihrem Dienstverhältnis</strong>
              </FormLegend>
            </Grid>
            <Group
              type="radio"
              name="employerType"
              question={translate(`${product.name}.employerType.question`, values)}
              options={[
                {
                  labelText: translate(`${product.name}.employerType.options.federal.label`, values),
                  labelIcon: <EmployerTypeFederalIcon />,
                  labelIconActive: <EmployerTypeFederalIcon active />,
                  value: "federal",
                },
                {
                  labelText: translate(`${product.name}.employerType.options.state.label`, values),
                  labelIcon: <EmployerTypeStateIcon />,
                  labelIconActive: <EmployerTypeStateIcon active />,
                  value: "state",
                },
                {
                  labelText: translate(`${product.name}.employerType.options.municipal.label`, values),
                  labelIcon: <EmployerTypeMunicipalIcon />,
                  labelIconActive: <EmployerTypeMunicipalIcon active />,
                  value: "municipal",
                },
              ]}
              validators={[requiredValidator(translate(`${product.name}.employerType.validators.required`, values))]}
            />
            {values.employerType === "state" && (
              <>
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>{translate(`${product.name}.employerState.question`, values)}</FormLegend>
                </Grid>
                <Grid size={12}>
                  <ValidatorSelectField
                    label={translate(`${product.name}.employerState.label`, values)}
                    name="employerState"
                    validators={[requiredValidator(translate(`${product.name}.employerState.validators.required`, values))]}
                  >
                    {employerStates.map((state) => (
                      <MenuItem key={state} value={state}>
                        {translate(`${product.name}.employerState.options.${state}.label`, values)}
                      </MenuItem>
                    ))}
                  </ValidatorSelectField>
                </Grid>
              </>
            )}

            {values.employerType === "municipal" && (
              <>
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>{translate(`${product.name}.employerMunicipality.question`, values)}</FormLegend>
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="employerMunicipality"
                    placeholder={translate(`${product.name}.employerMunicipality.placeholder`, values)}
                    label={translate(`${product.name}.employerMunicipality.label`, values)}
                    validators={[requiredValidator(translate(`${product.name}.employerMunicipality.validators.required`, values))]}
                    helperText={translate(`${product.name}.employerMunicipality.helperText`, values)}
                  />
                </Grid>
              </>
            )}

            <Group
              type="radio"
              name="objectiveClient"
              question={translate(`${product.name}.objectiveClient.question`, values)}
              options={[
                {
                  labelText: translate(`${product.name}.objectiveClient.options.moneyAndRefund.label`, values),
                  labelIcon: <ObjectiveMoneyAndRefundIcon />,
                  labelIconActive: <ObjectiveMoneyAndRefundIcon active />,
                  value: "moneyAndRefund",
                },
                {
                  labelText: translate(`${product.name}.objectiveClient.options.careerAndEmployment.label`, values),
                  labelIcon: <ObjectiveCareerAndEmploymentIcon />,
                  labelIconActive: <ObjectiveCareerAndEmploymentIcon active />,
                  value: "careerAndEmployment",
                },
              ]}
              validators={[requiredValidator(translate(`${product.name}.objectiveClient.validators.required`, values))]}
            />

            {values.objectiveClient && (
              <>
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>{translate(`${product.name}.authorityName.question${capitalizeFirstLetter(values.objectiveClient)}`, values)}</FormLegend>
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="authorityName"
                    placeholder={translate(`${product.name}.authorityName.placeholder${capitalizeFirstLetter(values.objectiveClient)}`, values)}
                    label={translate(`${product.name}.authorityName.label${capitalizeFirstLetter(values.objectiveClient)}`, values)}
                    validators={[requiredValidator(translate(`${product.name}.authorityName.validators.required`, values))]}
                    helperText={translate(`${product.name}.authorityName.helperText${capitalizeFirstLetter(values.objectiveClient)}`, values)}
                  />
                </Grid>

                {values.civilServiceStatus === "active" && (
                  <>
                    <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                      <FormLegend>{translate(`${product.name}.workLocationPostalCode.question`, values)}</FormLegend>
                    </Grid>
                    <Grid size={12}>
                      <ValidatorTextField
                        name="workLocationPostalCode"
                        label={translate(`${product.name}.workLocationPostalCode.label`, values)}
                        validators={[requiredValidator(translate(`${product.name}.workLocationPostalCode.validators.required`, values)), postalCodeDefault]}
                        helperText={translate(`${product.name}.workLocationPostalCode.helperText`, values)}
                      />
                    </Grid>
                  </>
                )}

                <Address
                  path="authorityAddress"
                  headline={translate(`${product.name}.authorityAddress.headline${capitalizeFirstLetter(values.objectiveClient)}`, values)}
                  info={
                    values.objectiveClient === "moneyAndRefund"
                      ? "Info: Bitte geben Sie die Behörde auf dem Briefkopf Ihrer letzten Bezügemitteilung (Gehaltsabrechnung) oder eines aktuellen Bescheids an."
                      : ""
                  }
                />
                <Grid size={12}>
                  <ValidatorTextField
                    name="authorityTelephone"
                    label={translate(`${product.name}.authorityTelephone.label${capitalizeFirstLetter(values.objectiveClient)}`, values)}
                    validators={[requiredValidator(translate(`${product.name}.authorityTelephone.validators.required`, values))]}
                  />
                </Grid>

                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>{translate(`${product.name}.personnelNumber.question`, values)}</FormLegend>
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="personnelNumber"
                    label={translate(`${product.name}.personnelNumber.label`, values)}
                    validators={[requiredValidator(translate(`${product.name}.personnelNumber.validators.required`, values))]}
                    helperText={translate(`${product.name}.personnelNumber.helperText`, values)}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
